import * as rx from "rxjs";
import componentService from "@/services/component";
import { parseComponent, ParseComponentError } from "./parse-catalog";
import { debounce } from "./timer";
import { fetchAllImagesInModules } from "./plano";

class LazyLoadController {
    constructor(loader) {
        let page = 0;
        let maxPage = 999;

        const valueSubject = new rx.BehaviorSubject([]);
        this.value = valueSubject.asObservable();

        const loadingSubject = new rx.BehaviorSubject(false);
        this.loading = loadingSubject.asObservable();

        this.filter = new rx.BehaviorSubject("");

        this.loadMore = async () => {
            if (page >= maxPage) return;
            try {
                loadingSubject.next(true);
                const promise = loader(page++, this.filter.value);
                this.pendingPromise = promise;
                const [result, _maxPage] = await promise;
                maxPage = _maxPage;
                // Make sure that the promise pushing new items is the latest called promise.
                // Since we may have more than one promise resolving in parallel, the last promise
                // may finish before the first promise, which will result in an unstable items list.
                if (this.pendingPromise === promise) {
                    valueSubject.next([...valueSubject.value, ...result]);
                    this.pendingPromise = undefined;
                }
            } finally {
                loadingSubject.next(false);
            }
        };

        // this will call loadMore immediatly
        const filterSub = this.filter.subscribe(() => {
            page = 0;
            maxPage = 999;
            valueSubject.next([]);
            this.loadMore();
        });

        this.close = () => {
            filterSub.unsubscribe();
        };
    }
}

async function fetchComponents({ page, brandId, zoneId, zones, campaignId, filter, systemTypeId, step, initialLocale, redirect }) {
    const response = await componentService.getAll({
        brandId,
        zoneId,
        zones,
        campaignId,
        page,
        limit: 50,
        systemTypeId,
        step,
        filter
    });

    const components = response.data;

    const result = [];
    for (const component of components) {
        try {
            const parsed = parseComponent(component, initialLocale, redirect);
            result.push(parsed);
        } catch (err) {
            if (err instanceof ParseComponentError) {
                console.warn("failed to parse component", err.message, component);
            } else {
                console.warn("failed to parse component", err, component);
            }
        }
    }
    return [result, response.meta.totalPage];
}

export default {
    data: () => ({
        catalogItems: {
            value: {},
            loading: false
        },
        modulesInCatalogImagesHashMap: new Map()
    }),
    mounted() {
        console.assert(!Context.endOfCatalogContent);
        Context.endOfCatalogContent = () => !this.catalogItems.loading && this.$catalogController.loadMore();
    },
    methods: {
        // `function` instead of () => {} or otherwise `this` won't work
        searchChanged: debounce(function(value) {
            this.$catalogController.filter.next(value);
        }, 250),
        setupCatalogPagination({ systemTypeId, brandId, zoneId, zones, campaignId, step }) {
            if (this.$loadingCatalogSub) this.$loadingCatalogSub.unsubscribe();
            if (this.$valueCatalogSub) this.$valueCatalogSub.unsubscribe();
            if (this.$catalogController) this.$catalogController.close();

            this.$loadingCatalogSub = undefined;
            this.$valueCatalogSub = undefined;
            this.$catalogController = undefined;

            this.$catalogController = new LazyLoadController((page, filter) => {
                if (!zoneId && (!zones || zones.length === 0)) {
                    return [[], 0];
                }
                return fetchComponents({
                    page,
                    filter,
                    systemTypeId,
                    brandId,
                    zoneId,
                    zones,
                    campaignId,
                    step,
                    redirect: path => window.open(path, "_blank"),
                    initialLocale: this.$locale
                });
            });
            this.$loadingCatalogSub = this.$catalogController.loading.subscribe(value => (this.catalogItems.loading = value));
            this.$valueCatalogSub = this.$catalogController.value.subscribe(value => {
                const result = {};

                for (const component of value) {
                    result[component.id] = component;
                    const images = [...fetchAllImagesInModules(component)];
                    for (const image of images) {
                        this.modulesInCatalogImagesHashMap.set(image, component.data);
                    }
                }
                this.catalogItems.value = result;
            });
        }
    },
    beforeDestroy() {
        Context.endOfCatalogContent = undefined;
        this.$loadingCatalogSub && this.$loadingCatalogSub.unsubscribe();
        this.$valueCatalogSub && this.$valueCatalogSub.unsubscribe();
        this.$catalogController && this.$catalogController.close();
    }
};



// WEBPACK FOOTER //
// ./src/plano/utils/lazy-load-controller.js